import React, { useEffect, useState } from 'react'

import { useLocale, useT } from '../../i18n'
import { CAPACITY_STATUS_COLOR, capacityStatusWordKey, type CapacityStatus } from '../../utils/carryingCapacity'
import { loadLotCapacityChecks, type LotCapacityCheckRow } from '../../utils/reportDatasets'

// Agua (findings #151): where flow is measured, show it against the lot's
// Capacidad de carga table — L/min per kg (fish) or per 1,000 (eggs / yolk-sac
// alevins) — in green / amber / red. Nothing shows while there is no table,
// no flow reading or no stock to compare with.

export type WaterCapacityStatusProps = {
  lotIds: string[]
  /** Changes when Agua saves or refreshes, to re-read the readings. */
  refreshKey: unknown
}

const worst = (statuses: Array<CapacityStatus | null>): CapacityStatus | null => {
  if (statuses.includes('red')) return 'red'
  if (statuses.includes('amber')) return 'amber'
  return statuses.includes('ok') ? 'ok' : null
}

export default function WaterCapacityStatus({ lotIds, refreshKey }: WaterCapacityStatusProps) {
  const t = useT()
  const locale = useLocale()
  const [rows, setRows] = useState<LotCapacityCheckRow[]>([])
  const lotIdsKey = lotIds.join('|')

  useEffect(() => {
    let cancelled = false
    const ids = lotIdsKey ? lotIdsKey.split('|') : []
    if (ids.length === 0) {
      setRows([])
      return
    }
    void loadLotCapacityChecks({ lotIds: ids })
      .then((loaded) => { if (!cancelled) setRows(loaded) })
      .catch((error) => {
        console.warn('[water] capacity check failed', error)
        if (!cancelled) setRows([])
      })
    return () => {
      cancelled = true
    }
  }, [lotIdsKey, refreshKey])

  const number = (value: number | null, digits: number): string => (
    value === null ? '—' : value.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })
  )
  const day = (value: string | null): string => (value ? value.split('-').reverse().join('/') : '—')

  const lines = rows.flatMap((row) => {
    if (!row.tableName || row.flowLpm === null) return []
    const { check } = row
    if (row.hatchery) {
      if (check.flowPerThousandLpm === null || check.hatcheryMinLpmPerThousand === null) return []
      return [{
        row,
        status: check.hatcheryFlowStatus,
        text: t('water.capacity.hatchery', {
          lot: row.lotKey,
          flow: number(row.flowLpm, 1),
          date: day(row.flowDate),
          perThousand: number(check.flowPerThousandLpm, 2),
          minimum: number(check.hatcheryMinLpmPerThousand, 2),
          table: row.tableName
        })
      }]
    }
    if (check.flowLpmPerKg === null || check.minFlowLpmPerKg === null) return []
    return [{
      row,
      status: worst([check.flowStatus]),
      text: t('water.capacity.fish', {
        lot: row.lotKey,
        flow: number(row.flowLpm, 1),
        date: day(row.flowDate),
        perKg: number(check.flowLpmPerKg, 2),
        minimum: number(check.minFlowLpmPerKg, 2),
        temperature: number(row.temperatureC, 1),
        table: row.tableName
      })
    }]
  })

  if (lines.length === 0) return null
  return (
    <div data-testid="water-capacity-status" role="status" style={{ margin: '10px 0', display: 'grid', gap: 4, fontSize: 13 }}>
      {lines.map(({ row, status, text }) => (
        <span
          key={row.lotId}
          data-testid={`water-capacity-${row.lotId}`}
          data-status={status ?? undefined}
          style={{ color: status ? CAPACITY_STATUS_COLOR[status] : '#475569', fontWeight: status && status !== 'ok' ? 700 : 600 }}
        >
          {status === 'red' ? '● ' : status === 'amber' ? '● ' : ''}{text}
          {(() => {
            // Findings #163: every Agua line is a flow check (against a minimum).
            const word = capacityStatusWordKey(status, 'minimum')
            return word ? ` · ${t(word)}` : ''
          })()}
        </span>
      ))}
    </div>
  )
}
